<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :header="`Generate Player & Ads.txt - ${seller?.name || 'Seller'}`"
    :style="{ width: '70rem' }"
    :breakpoints="{ '1199px': '90vw', '575px': '95vw' }"
  >
    <div class="flex flex-column gap-4">
      <!-- Player Setup -->
      <div class="section">
        <h3 class="mt-0">Player Setup</h3>
        <div class="grid">
          <!-- Player Layout -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label>*Player Layout</label>
            <Dropdown
              v-model="playerConfig.layout"
              :options="layoutOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Layout"
            />
          </div>

          <!-- Player Width -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label>*Player Width</label>
            <div class="p-inputgroup">
              <InputNumber
                v-model="playerConfig.width"
                :min="300"
                :max="1200"
              />
              <span class="p-inputgroup-addon">px</span>
            </div>
          </div>

          <!-- Thumbnail Color -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label>Thumbnail Color Scheme</label>
            <div class="flex gap-2 align-items-center">
              <ColorPicker v-model="playerConfig.color_scheme" />
              <InputText
                v-model="playerConfig.color_scheme"
                placeholder="HEX code (e.g., FF5733)"
                class="flex-1"
                maxlength="6"
              />
              <Tag
                v-if="playerConfig.color_scheme?.length"
                style="cursor: pointer"
                icon="pi pi-times"
                severity="secondary"
                value="Clear"
                rounded
                @click="playerConfig.color_scheme = ''"
              />
            </div>
          </div>
        </div>

        <!-- Player Code Display -->
        <div class="mt-3">
          <div class="flex justify-content-between align-items-center mb-2">
            <label class="block font-semibold">Player Embed Code</label>
            <div class="flex gap-2">
              <Button
                icon="pi pi-copy"
                label="Copy Code"
                size="small"
                outlined
                @click="copyToClipboard(playerCode, 'Player code')"
              />
              <SelectButton
                v-model="previewMode"
                :options="previewOptions"
                optionLabel="label"
                optionValue="value"
                size="small"
              />
            </div>
          </div>

          <!-- Code View -->
          <div v-if="previewMode === 'code'" class="code-container">
            <pre><code>{{ playerCode }}</code></pre>
          </div>

          <!-- Demo Preview (Default Values) -->
          <div
            v-else-if="previewMode === 'demo'"
            class="live-preview-container"
          >
            <div class="flex flex-column w-full">
              <div class="text-center mb-2">
                <small class="text-600"
                  >Demo Preview - Default Seller (5000/1183)</small
                >
              </div>
              <iframe
                :srcdoc="demoPlayerHtmlDoc"
                :style="{
                  width: '100%',
                  height: '500px',
                  border: 'none',
                  borderRadius: '8px',
                }"
                scrolling="no"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      <!-- Ads.txt Setup -->
      <div class="section">
        <h3 class="mt-0">Ads.txt File</h3>

        <!-- Ads.txt Options -->
        <div class="grid">
          <!-- Priority MultiSelect -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label>Priority</label>
            <MultiSelect
              v-model="adsTxtConfig.priority"
              :options="priorityOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Priorities"
              class="w-full"
            />
          </div>

          <!-- Type (Auto-detected based on seller_type) -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label>Type (Auto-detected)</label>
            <InputText :value="sellerTypeDisplay" disabled class="p-disabled" />
          </div>
        </div>

        <!-- Ads.txt Code Display -->
        <div class="mt-3">
          <div class="flex justify-content-between align-items-center mb-2">
            <label class="block font-semibold">Ads.txt Lines</label>
            <Button
              icon="pi pi-copy"
              label="Copy Ads.txt"
              size="small"
              outlined
              @click="copyToClipboard(adsTxtCode, 'Ads.txt')"
            />
          </div>

          <div class="code-container">
            <pre><code>{{ adsTxtCode }}</code></pre>
          </div>

          <small class="block mt-2 text-600">
            Add these lines to: <strong>{{ seller?.domain }}/ads.txt</strong>
          </small>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between w-full">
        <Button label="Cancel" outlined @click="closeDialog" />
        <Button
          label="Save as File"
          icon="pi pi-download"
          @click="saveAsFile"
          :loading="loading"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from "vue";
import { useToast } from "primevue/usetoast";
import axios from "axios";
import { API_BASE_URL, adsTxtFixed } from "@/consts";
import Dialog from "primevue/dialog";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import SelectButton from "primevue/selectbutton";
import ColorPicker from "primevue/colorpicker";
import Tag from "primevue/tag";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  seller: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:visible"]);
const toast = useToast();

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

const loading = ref(false);
const groupDisplayId = ref(null);
const adsTxtRows = ref([]);
const previewMode = ref("code"); // code | live | demo

const layoutOptions = [
  { label: "Low Visible (Horizontal)", value: "0" },
  { label: "High Visible (Vertical)", value: "1" },
];

const previewOptions = [
  { label: "Code", value: "code" },
  { label: "Demo", value: "demo" },
];

const playerConfig = ref({
  layout: "1",
  width: 600,
  color_scheme: "",
});

const adsTxtConfig = ref({
  priority: [1, 2, 3], // All priorities by default (user can change)
  // content_type is auto-detected from seller_type, not stored here
});

const priorityOptions = [
  { label: "Low", value: 3 },
  { label: "High", value: 2 },
  { label: "Critical", value: 1 },
];

// Auto-detect seller type from seller object
const sellerTypeDisplay = computed(() => {
  const sellerType = props.seller?.seller_type || "audio";
  if (sellerType === "video" || sellerType === "audio_video") {
    return "Audio + Video";
  }
  return "Audio Only";
});

// Auto-determine content types based on seller_type
const autoContentTypes = computed(() => {
  const sellerType = props.seller?.seller_type || "audio";
  if (sellerType === "video") {
    return ["audio", "display", "video"];
  }
  return ["audio", "display"];
});

// ✅ Player Code (shown by default)
const playerCode = computed(() => {
  // ✅ seller = partner_id, subseller = seller_id
  const partnerId = props.seller?.partner_id || 0;
  const sellerId = props.seller?.id || 0;
  const domain = props.seller?.domain || "example.com";
  const layout = playerConfig.value.layout;
  const width = playerConfig.value.width;
  const colorScheme = playerConfig.value.color_scheme;

  let playerTag = `<jamx-player seller="${partnerId}" subseller="${sellerId}" domain="${domain}" layout="${layout}" width="${width}" hide_slogan`;

  if (colorScheme?.length) {
    playerTag += ` color_scheme="#${colorScheme}"`;
  }

  playerTag += `></jamx-player>`;

  const scriptTag =
    '<script src="https://wgt.podcastswaves.com/jamx.player.js"><\/script>';
  return `${playerTag}\n${scriptTag}`;
});

// ✅ Demo player with default values (5000/1183)
const demoPlayerHtmlDoc = computed(() => {
  const layout = playerConfig.value.layout;
  const width = playerConfig.value.width;
  const colorScheme = playerConfig.value.color_scheme;

  let playerTag = `<jamx-player seller="5000" subseller="1183" domain="jamx.ai" layout="${layout}" width="${width}" hide_slogan`;

  if (colorScheme?.length) {
    playerTag += ` color_scheme="#${colorScheme}"`;
  }

  playerTag += `></jamx-player>`;

  const scriptTag =
    '<script src="https://wgt.podcastswaves.com/jamx.player.js"><\/script>';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { margin: 0; padding: 0; overflow: hidden; background: #f5f5f5; }
  </style>
</head>
<body>
  ${playerTag}
  ${scriptTag}
</body>
</html>`;
});

// ✅ Ads.txt file with filters (priority from user + auto-detected content type)
const adsTxtCode = computed(() => {
  // Fixed line using group_id and group_display_id (same as GenerateTagDialog)
  const fixedLine = adsTxtFixed(
    props.seller?.seller_group_id,
    groupDisplayId.value
  );

  // Filter by priority (user choice) and content type (auto-detected from seller_type)
  const filteredRows = adsTxtRows.value.filter(
    (item) =>
      autoContentTypes.value.includes(item.type) &&
      adsTxtConfig.value.priority.includes(item.priority)
  );

  const demandLines = filteredRows.map((row) => row.row_string).join("\n");

  return `${fixedLine}\n${demandLines}`;
});

// ✅ Fetch group display_id and ads.txt rows
const fetchData = async () => {
  if (!props.seller?.seller_group_id) return;

  try {
    loading.value = true;
    const token = localStorage.getItem("adminToken");

    // 1. Fetch group to get display_id
    const groupResponse = await axios.get(
      `${API_BASE_URL}/seller-group/${props.seller.seller_group_id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (groupResponse.data?.sellerGroup) {
      groupDisplayId.value = groupResponse.data.sellerGroup.display_id;
    }

    // 2. Fetch ads.txt rows
    const adsTxtResponse = await axios.get(`${API_BASE_URL}/adstxt-rows`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (adsTxtResponse.data?.result) {
      adsTxtRows.value = adsTxtResponse.data.result;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail: "Could not fetch complete ads.txt data",
      life: 3000,
    });
  } finally {
    loading.value = false;
  }
};

// ✅ Copy to clipboard
const copyToClipboard = (text, label) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.add({
        severity: "success",
        summary: "Copied!",
        detail: `${label} copied to clipboard`,
        life: 2000,
      });
    })
    .catch(() => {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to copy to clipboard",
        life: 3000,
      });
    });
};

// ✅ Save as file
const saveAsFile = () => {
  try {
    loading.value = true;

    const content = `========================================
PLAYER EMBED CODE
========================================

${playerCode.value}

Integration Guide: https://docs.jamx.ai/player-integration


========================================
ADS.TXT LINES
========================================

${adsTxtCode.value}


Note: Add these lines to your site's ads.txt file at:
${props.seller?.domain}/ads.txt
`;

    const blob = new Blob([content], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${props.seller?.domain || "seller"}_player_adstxt.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.add({
      severity: "success",
      summary: "Success",
      detail: "File downloaded successfully!",
      life: 3000,
    });

    closeDialog();
  } catch (error) {
    console.error("Error saving file:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to save file",
      life: 3000,
    });
  } finally {
    loading.value = false;
  }
};

const closeDialog = () => {
  isVisible.value = false;
  previewMode.value = "code"; // Reset to code view
};

// Watch for dialog open
watch(isVisible, (newValue) => {
  if (newValue) {
    // Reset config
    playerConfig.value = {
      layout: "1",
      width: 600,
      color_scheme: "",
    };
    adsTxtConfig.value = {
      priority: [1, 2, 3],
    };
    previewMode.value = "code"; // Reset to code view

    // Fetch data
    fetchData();
  }
});
</script>

<style scoped>
.section {
  padding: 1.5rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.code-container {
  background: var(--surface-0);
  color: var(--text-color-secondary);
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  padding: 1rem;
  max-height: 400px;
  overflow: auto;
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
  position: relative;
}

.code-container pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.code-container code {
  color: inherit;
}

.live-preview-container {
  background: var(--surface-card);
  border: 2px solid var(--primary-color);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  justify-content: center;
  min-height: 400px;
  align-items: center;
}
</style>
